import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useAuth } from './AuthContext';

const XPContext = createContext({});

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const XPProvider = ({ children }) => {
    const { session } = useAuth();
    const [xpStatus, setXPStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pendingNotifications, setPendingNotifications] = useState([]);

    // Fetch XP status from backend
    const fetchXPStatus = useCallback(async () => {
        if (!session?.access_token) {
            console.log('[XPContext] No access token, skipping XP fetch');
            setLoading(false);
            return;
        }

        try {
            console.log('[XPContext] Fetching XP status...');
            const response = await fetch(`${API_URL}/api/xp/status`, {
                headers: { 'Authorization': `Bearer ${session.access_token}` }
            });

            if (response.ok) {
                const data = await response.json();
                console.log('[XPContext] XP status loaded:', data);
                setXPStatus(data);
            } else {
                // Log HTTP errors (401, 500, etc.)
                const errorText = await response.text();
                console.error('[XPContext] Failed to fetch XP status:', response.status, errorText);
            }
        } catch (err) {
            console.error('[XPContext] Network error fetching XP status:', err);
        } finally {
            setLoading(false);
        }
    }, [session?.access_token]);

    // Fetch XP status on mount and when session changes
    useEffect(() => {
        if (session?.access_token) {
            fetchXPStatus();
        } else {
            setXPStatus(null);
            setLoading(false);
        }
    }, [session?.access_token, fetchXPStatus]);

    // Method to notify of XP gain (call from activity completions)
    const notifyXPGain = useCallback((xpResult) => {
        if (xpResult && xpResult.xp_awarded > 0) {
            // Add to pending notifications queue
            setPendingNotifications(prev => [...prev, xpResult]);

            // Update local XP state immediately for responsive UI
            setXPStatus(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    level: xpResult.current_level,
                    level_title: xpResult.level_title,
                    current_xp: xpResult.xp_this_level,
                    next_level_xp: xpResult.xp_for_next_level,
                    total_xp: xpResult.total_xp,
                    progress_percent: xpResult.xp_for_next_level > 0
                        ? (xpResult.xp_this_level / xpResult.xp_for_next_level) * 100
                        : 100
                };
            });
        }
    }, []);

    // Dismiss the oldest notification
    const dismissNotification = useCallback(() => {
        setPendingNotifications(prev => prev.slice(1));
    }, []);

    // Clear all notifications
    const clearNotifications = useCallback(() => {
        setPendingNotifications([]);
    }, []);

    const value = {
        xpStatus,
        loading,
        refreshXP: fetchXPStatus,
        notifyXPGain,
        pendingNotifications,
        dismissNotification,
        clearNotifications
    };

    return (
        <XPContext.Provider value={value}>
            {children}
        </XPContext.Provider>
    );
};

export const useXP = () => {
    const context = useContext(XPContext);
    if (!context) {
        throw new Error('useXP must be used within XPProvider');
    }
    return context;
};
